/**
 * Scroll-parallax for the "Boxes" content element.
 *
 * All boxes inside the grid move together at the same speed, slightly faster
 * than the page scroll, creating a subtle floating / depth effect. The effect
 * is disabled below the `md` breakpoint (768px) to avoid jarring motion on
 * small screens where boxes stack vertically.
 *
 * Usage in the Fluid template (one wrapper per grid variant):
 *   <div class="grid ..." x-data="ymhBoxesParallax" x-init="update()"
 *        @scroll.window="update()">
 *     <div data-parallax>...</div>
 *   </div>
 *
 * BUG FIX (2026-07): The original implementation used `window.scrollY *
 * PARALLAX_SPEED` as absolute transform value. On long pages, boxes deep in
 * the document accumulated large translateY offsets and visually merged with
 * the element above them (and blurred any element below when the boxes
 * scrolled far up over them). The fix uses an ELEMENT-RELATIVE parallax:
 * we measure how far the grid center has passed the viewport center and
 * translate proportionally. When the grid is far outside the viewport, the
 * transform is reset to 0. This keeps the effect subtle and locally
 * contained to the box element itself.
 *
 * Blur fix: integer pixel values (Math.round) instead of sub-pixel `.toFixed(1)`
 * avoid the browser's sub-pixel compositor blur, which was making text and
 * images inside the box look fuzzy.
 */
const PARALLAX_SPEED = 0.08;
const PARALLAX_RANGE_PX = 40; // max total translation range in either direction
const MIN_VIEWPORT_WIDTH = 768;

export function boxesParallaxDataContext() {
  return {
    update(this: { $el: HTMLElement }) {
      const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      const isMobile = window.innerWidth < MIN_VIEWPORT_WIDTH;

      const boxes = this.$el.querySelectorAll<HTMLElement>("[data-parallax]");

      if (isMobile || reduceMotion) {
        boxes.forEach((el) => {
          el.style.transform = "";
        });
        return;
      }

      // Element-relative parallax: measure how far the grid center has
      // moved past the viewport center. When the grid is scrolled into
      // view (moving up), boxes translate slightly UP relative to their
      // natural position — a subtle floating effect that stays local.
      const rect = this.$el.getBoundingClientRect();
      const viewportH = window.innerHeight;
      const gridCenter = rect.top + rect.height / 2;
      const viewportCenter = viewportH / 2;

      // Signed distance: negative when grid is below viewport center,
      // positive when grid has scrolled above center.
      const delta = viewportCenter - gridCenter;

      // Apply speed factor and clamp to a small range so the boxes never
      // wander far from their natural position.
      let offset = delta * PARALLAX_SPEED;
      if (offset > PARALLAX_RANGE_PX) offset = PARALLAX_RANGE_PX;
      if (offset < -PARALLAX_RANGE_PX) offset = -PARALLAX_RANGE_PX;

      // Integer rounding to avoid sub-pixel compositor blur on text/images.
      const px = Math.round(offset);

      boxes.forEach((el) => {
        el.style.transform = `translate3d(0, ${px}px, 0)`;
      });
    },
  };
}
